import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useCreateServiceAccount } from "@/queries/service-account-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC, type FormEvent } from "react";
import { ServiceAccountFormFields } from "@/components/settings/organization/service-accounts/ServiceAccountFormFields";

type CreateServiceAccountDialogProps = {
    open: boolean;
    onClose: () => void;
    onSuccess?: (serviceAccountId: string) => void;
};

export const CreateServiceAccountDialog: FC<CreateServiceAccountDialogProps> = ({ open, onClose, onSuccess }) => {
    const { toast } = useToast();
    const createServiceAccount = useCreateServiceAccount();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!name.trim()) {
                return;
            }

            try {
                // Default to 1 year from now for validUntil
                const validUntil = new Date();
                validUntil.setFullYear(validUntil.getFullYear() + 1);

                const newServiceAccount = await createServiceAccount.mutateAsync({
                    name: name.trim(),
                    description: description.trim() || undefined,
                    validUntil,
                });

                toast({
                    title: "Service account created",
                    description: `Service account "${name}" has been created successfully.`,
                });

                // Reset form
                setName("");
                setDescription("");
                onClose();
                onSuccess?.(newServiceAccount.id);
            } catch (error) {
                toast({
                    title: "Failed to create service account",
                    description: formatError(error),
                });
            }
        },
        [name, description, createServiceAccount, toast, onClose, onSuccess],
    );

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                setName("");
                setDescription("");
                onClose();
            }
        },
        [onClose],
    );

    const isFormValid = name.trim().length >= 1 && name.trim().length <= 64;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                data-testid="create-service-account-dialog"
                data-track-location={TrackLocations.ServiceAccountCreateDialog}
                className="max-w-md"
            >
                <DialogHeader>
                    <DialogTitle>New Service Account</DialogTitle>
                    <DialogDescription>
                        Use them to run workflows, manage integrations, or automate actions without using a personal
                        account.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <form id="create-service-account-form" onSubmit={handleSubmit}>
                        <ServiceAccountFormFields
                            name={name}
                            description={description}
                            onNameChange={setName}
                            onDescriptionChange={setDescription}
                            autoFocus
                        />
                    </form>
                </DialogBody>

                <DialogFooter className="flex items-center justify-end">
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="create-service-account-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        form="create-service-account-form"
                        variant="primary"
                        disabled={!isFormValid || createServiceAccount.isPending}
                        loading={createServiceAccount.isPending}
                        data-testid="create-service-account-dialog-create"
                        data-tracking-id="create-service-account-dialog-create"
                    >
                        Create
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
